import { Divider, TextField } from '@mui/material';
import { ExerciseForm, Tag, TrainingType } from '@strength/domain';
import { useMemo, useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { v4 } from 'uuid';
import { environment } from '../../../../../environment';
import { trpc } from '../../../../../trpc/react';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '../../../../components/form/form';
import { MultiSelect } from '../../../../components/input/multi-select';
import { ImageUploader } from '../../../../components/input/multimedia/image-upload';
import { VideoUploader } from '../../../../components/input/multimedia/video-upload';
import { TeamSelect } from '../../../../components/layout/TeamSelect';
import { TrainingTypeSelect } from '../../../../components/layout/training-type-select';
import { useMetrics } from '../../../../hooks/useMetrics';
import { useToast } from '../../../../hooks/useToast';
import { BodyPart, Equipment, ExerciseFormTab } from '../types';
import { IntegrationProvidersSheet } from './IntegrationProvidersSheet';
import { MetricsSwitch } from './MetricsSwitch';

export const ExerciseFormContent = ({
  form,
  step,
  bodyPartOptions: _bodyPartOptions,
  equipmentOptions: _equipmentOptions,
  tagOptions: _tagOptions,
  trainingTypeOptions,
  hasInitialDefaultValue = false,
  showLibraryField,
  isReadOnly = false,
  requireMetadataFields = false,
}: {
  form: UseFormReturn<ExerciseForm>;
  step: ExerciseFormTab;
  bodyPartOptions: BodyPart[];
  equipmentOptions: Equipment[];
  tagOptions: Tag[];
  trainingTypeOptions: TrainingType[];
  hasInitialDefaultValue?: boolean;
  showLibraryField: boolean;
  isReadOnly: boolean;
  requireMetadataFields?: boolean;
}) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { control } = form;
  const [tagOptions, setTagOptions] = useState<Tag[]>(_tagOptions);
  const [bodyPartOptions, setBodyPartOptions] =
    useState<BodyPart[]>(_bodyPartOptions);
  const [equipmentOptions, setEquipmentOptions] =
    useState<Equipment[]>(_equipmentOptions);

  const { getMetrics } = useMetrics(false);
  const metrics = useMemo(() => {
    return getMetrics();
  }, [getMetrics]);

  const { data: integrationProviders } =
    trpc.exerciseLibrary.getIntegrationProviders.useQuery();

  return (
    <>
      {step === 'details' && (
        <div className="flex flex-col">
          <div className="flex flex-col space-y-4">
            <FormField
              control={control}
              name="name"
              render={({ field: { ref, ...field } }) => (
                <FormItem>
                  <FormControl>
                    <TextField
                      label={t('name')}
                      {...field}
                      ref={ref}
                      placeholder={t('enterName')}
                      autoFocus
                      disabled={isReadOnly}
                      onFocus={
                        hasInitialDefaultValue
                          ? (e) => e.target.select()
                          : undefined
                      }
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {showLibraryField && (
              <FormField
                control={control}
                name="teamId"
                render={({ field: { value, onChange } }) => (
                  <FormItem>
                    <FormControl>
                      <TeamSelect
                        teamId={value}
                        onSelect={onChange}
                        showBaseLibrary={true}
                        label={t('library')}
                        disabled={isReadOnly}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            <FormField
              control={control}
              name="trainingType"
              render={({ field: { value, onChange } }) => (
                <FormItem className="pt-6">
                  <FormControl>
                    <TrainingTypeSelect
                      label={t('exerciseType')}
                      trainingTypes={trainingTypeOptions}
                      selectedType={value}
                      onChange={onChange}
                      allowCreate={true}
                      disabled={isReadOnly}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={control}
              name="tags"
              render={({ field: { value, onChange }, fieldState: { error } }) => (
                <FormItem className="flex flex-col">
                  <FormControl>
                    <MultiSelect
                      label={t('tags')}
                      error={!!error}
                      selectedOptions={value}
                      options={tagOptions}
                      onChange={onChange}
                      optionToName={(option) => option.name}
                      placeholder={t('selectTags')}
                      disabled={isReadOnly}
                      createOption={(name) => {
                        const trimmed = name.trim();
                        if (!trimmed) return;
                        if (
                          tagOptions.some(
                            (t) =>
                              t.name.trim().toLocaleLowerCase() ===
                              trimmed.toLocaleLowerCase(),
                          )
                        ) {
                          toast({
                            title: t('tagAlreadyExists'),
                            severity: 'error',
                          });
                          return;
                        }
                        const newTag = {
                          id: v4(),
                          name: trimmed,
                          type: 'exercise' as const,
                        };
                        setTagOptions([...tagOptions, newTag]);
                        return newTag;
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <Divider sx={{ marginTop: '24px', marginBottom: '24px' }} flexItem />
          <div className="flex flex-col space-y-4">
            <FormField
              control={control}
              name="bodyParts"
              render={({ field: { value, onChange }, fieldState: { error } }) => (
                <FormItem className="flex flex-col">
                  <FormControl>
                    <MultiSelect
                      label={t('bodyParts')}
                      error={!!error}
                      selectedOptions={value}
                      options={bodyPartOptions}
                      onChange={onChange}
                      optionToName={(option) => t(option.name as any)}
                      placeholder={t('selectBodyParts')}
                      disabled={isReadOnly}
                      createOption={(name) => {
                        const trimmed = name.trim();
                        if (!trimmed) return;
                        const existing = bodyPartOptions.find(
                          (b) =>
                            b.name.trim().toLocaleLowerCase() ===
                            trimmed.toLocaleLowerCase(),
                        );
                        if (existing) return existing;
                        const newBodyPart = {
                          id: v4(),
                          name: trimmed,
                        };
                        setBodyPartOptions([...bodyPartOptions, newBodyPart]);
                        return newBodyPart;
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={control}
              name="equipment"
              render={({ field: { value, onChange }, fieldState: { error } }) => (
                <FormItem className="flex flex-col">
                  <FormControl>
                    <MultiSelect
                      label={t('equipment')}
                      error={!!error}
                      selectedOptions={value}
                      options={equipmentOptions}
                      onChange={onChange}
                      optionToName={(option) => t(option.name as any)}
                      placeholder={t('selectEquipment')}
                      disabled={isReadOnly}
                      createOption={(name) => {
                        const trimmed = name.trim();
                        if (!trimmed) return;
                        const existing = equipmentOptions.find(
                          (e) =>
                            e.name.trim().toLocaleLowerCase() ===
                            trimmed.toLocaleLowerCase(),
                        );
                        if (existing) return existing;
                        const newEquipment = {
                          id: v4(),
                          name: trimmed,
                        };
                        setEquipmentOptions([
                          ...equipmentOptions,
                          newEquipment,
                        ]);
                        return newEquipment;
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <Divider sx={{ marginTop: '24px', marginBottom: '24px' }} flexItem />
          <div className="flex flex-col space-y-4">
            <FormField
              control={form.control}
              name="imageUrl"
              render={({ field: { value, onChange } }) => (
                <FormItem>
                  <FormLabel>{t('thumbnailImage')}</FormLabel>
                  <FormControl>
                    <ImageUploader
                      onChange={onChange}
                      imageUrl={value}
                      filePrefix={t('libraryExercise')}
                      disabled={isReadOnly}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="videoUrl"
              render={({ field: { value, onChange } }) => (
                <FormItem>
                  <FormLabel>{t('exerciseVideo')}</FormLabel>
                  <FormControl>
                    <VideoUploader
                      onChange={onChange}
                      videoUrl={value}
                      filePrefix={t('libraryExercise')}
                      disabled={isReadOnly}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>
      )}

      {step === 'metrics' && (
        <div className="flex flex-col space-y-4 pt-2">
          <FormField
            control={control}
            name="defaultMetrics"
            render={({ field: { value, onChange } }) => (
              <FormItem>
                <FormLabel>{t('metrics')}</FormLabel>
                <FormControl className="grid grid-flow-col">
                  <MetricsSwitch
                    metricsOptions={metrics}
                    selectedMetrics={value ?? []}
                    setSelectedMetrics={onChange}
                    disabled={isReadOnly}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          {!environment.VITE_IS_GOV && (
            <FormField
              control={control}
              name="integrationProviderProperties"
              render={({ field: { value, onChange } }) => (
                <FormItem>
                  <FormLabel>{t('integrationSettings')}</FormLabel>
                  <FormControl>
                    <IntegrationProvidersSheet
                      integrationProperties={value}
                      onChange={onChange}
                      integrationProviderOptions={integrationProviders ?? []}
                      disabled={isReadOnly}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}
        </div>
      )}
    </>
  );
};
